import { useMemo, useState } from 'react'
import { cn } from '@/utils'

import { DraggableTreeNodeContainer } from './DraggableTreeNodeContainer'
import { TreeNodeItem } from './TreeNodeItem'

import { useShallow } from 'zustand/react/shallow'
import { useSelectable } from '@/pages/CADPage/hooks'
import { useCADPageStore } from '@/state'
import { useIsAssemblyNodeLocked } from './hooks/useIsAssemblyNodeLocked'

import type { RawAssemblyTree, ASMTreeNode } from '@/state'
import type { GLTFObject } from '@/lib/cad/GLTFObject'
import type { DocumentTypeChoices } from '@/lib/api/client'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { RenameTreeNodeInput } from './RenameTreeNodeInput'

export const TreeNode = ({
  node,
  tree,
  gltf,
  level,
  isReadOnly,
  documentType,
}: {
  node: ASMTreeNode
  tree: RawAssemblyTree
  gltf: GLTFObject
  level: number
  isReadOnly: boolean
  documentType: DocumentTypeChoices
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [showHidePartButton, setShowHidePartButton] = useState(false)

  const selectable = useSelectable({})

  const hiddenParts = useCADPageStore((state) => state.hiddenParts)
  const isHidden = hiddenParts.includes(node.uuid)

  const selectedParts = useCADPageStore(
    useShallow((state) => state.selectedParts),
  )
  const isSelected = useMemo(
    () => selectedParts.includes(node.uuid),
    [selectedParts, node.uuid],
  )

  const {
    data: { isLocked },
  } = useIsAssemblyNodeLocked({ node })

  const isDraggable =
    documentType === 'work_instructions' &&
    !!node.parent &&
    !isReadOnly &&
    !isLocked

  if (isEditing) {
    return (
      <div
        className="select-none cursor-text flex items-center justify-between space-x-0 py-3 mx-1"
        style={{
          paddingLeft: (level - 1) * 10,
        }}
      >
        <RenameTreeNodeInput
          tree={tree}
          node={node}
          isEditing={isEditing}
          onRename={() => {
            setIsEditing(false)
          }}
        />
      </div>
    )
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger disabled={isReadOnly}>
        <DraggableTreeNodeContainer
          className={cn(
            'hover:bg-primary-10 rounded-lg border border-transparent',
            {
              'hover:bg-primary-20 ': level > 1,
              'hover:bg-primary-20 bg-primary-20': isSelected && isDraggable,
              'select-none cursor-move': isDraggable,
              'bg-gray-100 text-gray-600': isHidden && !isSelected,
            },
          )}
          enabled={isDraggable}
          node={node}
          tree={tree}
          onMouseOver={(e, node) => {
            e.stopPropagation()
            setShowHidePartButton(true)
            selectable
              .handlers(node.uuid, 'assembly', { internalView: true })
              .onSelectHover(e)
          }}
          onMouseOut={(e) => {
            e.stopPropagation()
            setShowHidePartButton(false)
            selectable.onSelectEnd(e)
          }}
        >
          <TreeNodeItem
            tree={tree}
            node={node}
            gltf={gltf}
            isCollapsible={node.children.length > 0}
            isAttachedToOperationStep={isLocked}
            level={level}
            showHidePartButton={showHidePartButton}
          />
        </DraggableTreeNodeContainer>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          onMouseDown={(e) => {
            e.stopPropagation()
            setIsEditing(true)
          }}
        >
          Rename
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}
